import type { ElevatorSurvey } from './form/form'

/**
 * Fields required on every elevator survey regardless of type. These gate
 * submission because they identify the unit, select the correct calculation
 * branch, and schedule the eMCP.
 *
 * `siteName` is intentionally NOT here: the site doc (`sites/{siteId}.name`)
 * is the source of truth, the field is hidden on the form, and the MCP
 * generator never reads it. Requiring it on the survey added no value and
 * blocked super-admin edits when the site doc lacked a name (MCP-632).
 */
export const ALWAYS_REQUIRED_SURVEY_FIELDS = [
  'ahjId',
  'buildingDesignation',
  'elevatorType',
  'siteId',
  'startDateMonth',
  'startDateYear',
] as const satisfies readonly (keyof ElevatorSurvey)[]

/**
 * Every survey field used to select maintenance items or calculate
 * maintenance intervals, that also appears in at least one elevator-type
 * layout. Sourced from `functions/src/maintenance/mcpGenerator.ts` (scoring
 * inputs used by `calculateTotal` plus special per-item interval rules) and
 * `functions/src/maintenance/itemRepository.ts` (boolean and string
 * item-filter criteria).
 *
 * Fields used only to compute per-item minutes (`typeOfMast`,
 * `numberOfMastSections`, `numberOfMastTies`, `numberOfMotors`,
 * `pitPermitRequired`) are intentionally excluded: the generator falls back
 * to sensible defaults, and leaving them blank only changes how long a task
 * takes — not which tasks are scheduled or how often.
 *
 * Fields referenced by the calculation but absent from every layout are
 * also excluded: they cannot be collected via a survey, so requiring them
 * would be impossible to satisfy.
 *
 * For a given layout, the required fields are
 * `ALWAYS_REQUIRED_SURVEY_FIELDS ∪ (INITIAL_MCP_GENERATION_FIELDS ∩
 * layout-fields)`. Computed in `form/layouts/helpers.ts`.
 */
export const INITIAL_MCP_GENERATION_FIELDS = [
  'accumulatedWear',
  'acopumdType',
  'brakeType',
  'bufferType',
  'carDoorType',
  'condition',
  'design',
  'doorPanelsType',
  'doorRestrictorType',
  'driveType',
  'drivingMachineInstallYear',
  'environment',
  'governorType',
  'guideType',
  'hasAntiSlideDevices',
  'hasBatteryLowering',
  'hasBrokenRopeSelectorSwitch',
  'hasBrokenSuspensionDetection',
  'hasBufferSwitches',
  'hasCarSafeties',
  'hasCeilingGuards',
  'hasCombImpactDetection',
  'hasCompensation',
  'hasCompensationSheave',
  'hasCounterWeights',
  'hasCounterweightSafeties',
  'hasCylinderCorrosionProtection',
  'hasDriveChainDeviceSwitch',
  'hasEmergencyPower',
  'hasFirefighterService',
  'hasFlexibleHose',
  'hasHandrailSpeedMonitoring',
  'hasHoistwayAccess',
  'hasLevelPalletDevice',
  'hasMGSet',
  'hasMissingPalletDevice',
  'hasNonMetallicSheaveLiner',
  'hasPlungerGripper',
  'hasPressureSwitch',
  'hasPressureVessel',
  'hasSeismicDevices',
  'hasTractionLossDetection',
  'hasUnderPlatformObstructionDevice',
  'hasVisionPanels',
  'hoistwayDoorType',
  'inherentQuality',
  'interlocksInstallYear',
  'isMachineRoomLess',
  'isObservationElevator',
  'numLandings',
  'numberOfOpenings',
  'operationalControllerInstallYear',
  'speed',
  'suspensionMeansType',
  'suspensionMembersInstallYear',
  'technology',
  'typeOfDoorOperator',
  'typeOfMotorController',
  'typeOfUse',
  'usage',
] as const satisfies readonly (keyof ElevatorSurvey)[]
